import React, { useState, useRef, useCallback, useMemo } from 'react';
import cx from 'classnames';
import useIsMounted from '../Reader/Phrase/hooks/useIsMounted';
import { NO_RESPONSE } from '../../constants/constants';
import useFormativeScreen from '../Formative/useFormativeScreen';
import { useContainerHeightVar } from '../Formative/containerHeight';
import { nextFrame } from '../Formative/formativeScreenConstants';
import BucketingPromptBar from './BucketingPromptBar';
import BucketingMediaSection from './BucketingMediaSection';
import BucketingBoard from './BucketingBoard';
import BucketingPool from './BucketingPool';
import BucketingSubmitButton from './BucketingSubmitButton';
import { LOG, zoneCapacity, encodePlacements, groupIntoRows } from './bucketingConstants';

const EMPTY_PLACEMENTS = {};

const Bucketing = ({
  speech,
  options,
  dropZones,
  videoURL,
  imageURL,
  readOptionsAloud,
  isInstruction,
  isRepeating,
  onDone,
  shouldExtendTimeout,
  timeoutExtensionMultiplier,
  idleTimeoutOverrides,
  idleEncouragementText,
  instructionRepeatable,
  instructionMaxRepeats,
  instructionRepeatVariation,
  repeatInstructions,
  handleRepeatInstructions,
  beginOverlayRepeatPlayback,
  endOverlayRepeatPlayback,
  setHideParentRepeatButton,
  currentTriggerAction,
  currentTriggerData,
  onTriggerActionComplete,
  deferPoolNarration,
  verbalInstructions,
}) => {
  const pool = useMemo(() => options || [], [options]);
  const { rows, zones } = useMemo(() => groupIntoRows(dropZones), [dropZones]);

  const poolByKey = useMemo(() => {
    const byKey = {};
    for (const poolItem of pool) byKey[poolItem.key] = poolItem;
    return byKey;
  }, [pool]);

  const [placements, setPlacements] = useState(EMPTY_PLACEMENTS);
  const [selectedPoolKey, setSelectedPoolKey] = useState(null);

  const isMountedRef = useIsMounted();
  const containerRef = useRef(null);
  useContainerHeightVar(containerRef, '--bkt-h');

  const placementsRef = useRef(EMPTY_PLACEMENTS);
  placementsRef.current = placements;

  const placedKeys = useMemo(() => {
    const keys = new Set();
    for (const zoneKey of Object.keys(placements)) {
      for (const poolKey of placements[zoneKey]) keys.add(poolKey);
    }
    return keys;
  }, [placements]);

  const boardIsDirty = placedKeys.size > 0;

  const clearBoard = useCallback(() => setPlacements(EMPTY_PLACEMENTS), []);
  const clearSelection = useCallback(() => setSelectedPoolKey(null), []);

  const encodeResponse = useCallback(() => {
    const placed = placementsRef.current;
    const anythingPlaced = Object.keys(placed).some((zoneKey) => placed[zoneKey].length > 0);
    return anythingPlaced ? encodePlacements(zones, placed) : NO_RESPONSE;
  }, [zones]);

  const screen = useFormativeScreen({
    name: 'bucketing',
    log: LOG,
    speech,
    pool,
    hasImage: !!imageURL,
    hasVideo: !!videoURL,
    readOptionsAloud,
    isInstruction,
    isRepeating,
    shouldExtendTimeout,
    timeoutExtensionMultiplier,
    idleTimeoutOverrides,
    idleEncouragementText,
    instructionRepeatable,
    instructionMaxRepeats,
    instructionRepeatVariation,
    repeatInstructions,
    handleRepeatInstructions,
    beginOverlayRepeatPlayback,
    endOverlayRepeatPlayback,
    setHideParentRepeatButton,
    currentTriggerAction,
    currentTriggerData,
    onTriggerActionComplete,
    deferPoolNarration,
    verbalInstructions,
    onClearBoard: clearBoard,
    onClearSelection: clearSelection,
    encodeResponse,
    onDone,
  });

  const {
    hidden,
    layout,
    boardDisabled,
    pointerTarget,
    encouragementText,
    resetIdleTimer,
    clearIdleTimer,
    narratePoolItem,
  } = screen;

  const narratePoolItemRef = useRef(narratePoolItem);
  narratePoolItemRef.current = narratePoolItem;
  const submittedRef = useRef(screen.submitted);
  submittedRef.current = screen.submitted;
  const selectionGenerationRef = useRef(0);

  const handlePoolItemClick = useCallback(
    async (poolItem) => {
      if (boardDisabled) return;
      selectionGenerationRef.current += 1;
      const generation = selectionGenerationRef.current;
      const isDeselect = selectedPoolKey === poolItem.key;
      setSelectedPoolKey(isDeselect ? null : poolItem.key);
      if (!isDeselect) clearIdleTimer();
      // abortSpeech setStates on Speech, so batching it with the selection above makes the
      // student wait out that subtree's render before the tile grows. A frame lets the
      // zoom paint first, and still lands the abort before the narration it must cut.
      await nextFrame();
      speech.abortSpeech?.();
      if (isDeselect) {
        resetIdleTimer(encouragementText);
        return;
      }
      await narratePoolItemRef.current(poolItem);
      if (!isMountedRef.current || submittedRef.current) return;
      // A second tap aborts this narration, which resolves the await above rather than
      // rejecting it. Restarting the idle timer from here would do it while the tap that
      // superseded us is still speaking.
      if (selectionGenerationRef.current !== generation) return;
      resetIdleTimer(encouragementText);
    },
    [
      boardDisabled,
      selectedPoolKey,
      speech,
      clearIdleTimer,
      resetIdleTimer,
      encouragementText,
      isMountedRef,
    ]
  );

  const handleBucketClick = useCallback(
    (bucket) => {
      if (boardDisabled) return;
      if (!selectedPoolKey) return;

      const capacity = zoneCapacity(bucket);
      setPlacements((previous) => {
        const next = {};
        for (const zoneKey of Object.keys(previous)) {
          next[zoneKey] = previous[zoneKey].filter((poolKey) => poolKey !== selectedPoolKey);
        }
        const existing = next[bucket.key] || [];
        const roomMade = existing.length >= capacity ? existing.slice(1) : existing;
        next[bucket.key] = [...roomMade, selectedPoolKey];
        return next;
      });
      setSelectedPoolKey(null);
      resetIdleTimer(encouragementText);
    },
    [boardDisabled, selectedPoolKey, resetIdleTimer, encouragementText]
  );

  const handleBucketClickRef = useRef(null);
  handleBucketClickRef.current = handleBucketClick;

  const handlePlacedItemClick = useCallback(
    (bucket, poolKey) => {
      if (boardDisabled) return;
      if (selectedPoolKey) {
        handleBucketClickRef.current(bucket);
        return;
      }
      setPlacements((previous) => ({
        ...previous,
        [bucket.key]: (previous[bucket.key] || []).filter((key) => key !== poolKey),
      }));
      setSelectedPoolKey(null);
      resetIdleTimer(encouragementText);
    },
    [boardDisabled, selectedPoolKey, resetIdleTimer, encouragementText]
  );

  return (
    <div
      ref={containerRef}
      className={cx('bucketing', {
        'bucketing--imageOnly': layout.imageOnly,
        'bucketing--videoOnly': layout.videoOnly,
        'bucketing--audioOnly': layout.audioOnly,
        'bucketing--thumbnail': !!imageURL && !videoURL && !hidden.media && !hidden.board,
      })}
    >
      <BucketingPromptBar
        hidePrompt={hidden.prompt}
        hideRepeat={hidden.repeat}
        onRepeatClick={screen.onRepeatClick}
        repeatDisabled={screen.repeatDisabled}
        showRepeatPointer={pointerTarget === 'repeat'}
      />

      <BucketingMediaSection
        hasVideo={!!videoURL}
        hasImage={!!imageURL}
        hideMedia={hidden.media}
        videoURL={videoURL}
        imageURL={imageURL}
        speech={speech}
        videoPlaying={screen.videoPlaying}
        maxVideoReplays={screen.maxVideoReplays}
        onVideoReplay={screen.consumeStimulusReplay}
        unmountVideoWhenDone={screen.unmountVideoWhenDone}
        suppressVideoReplayOverlay={screen.suppressVideoReplayOverlay}
        showRepeatOverlay={screen.showRepeatOverlay}
        showRepeatOverlayReplay={screen.showRepeatOverlayReplay}
        pointerTarget={pointerTarget}
        onRepeatOverlayReplay={screen.handleRepeatOverlayReplay}
        onRepeatOverlayNext={screen.handleRepeatOverlayNext}
        onVideoStart={screen.onVideoStart}
        onVideoDone={screen.onVideoDone}
      />

      <div className="bucketing__workspace">
        <BucketingBoard
          rows={rows}
          placements={placements}
          poolByKey={poolByKey}
          selectedPoolKey={selectedPoolKey}
          hideBoard={hidden.board}
          pointerTarget={pointerTarget}
          disabled={boardDisabled}
          onBucketClick={handleBucketClick}
          onPlacedItemClick={handlePlacedItemClick}
        />

        <BucketingPool
          pool={pool}
          placedKeys={placedKeys}
          revealedKeys={screen.revealedKeys}
          selectedPoolKey={selectedPoolKey}
          playingPoolKey={screen.playingPoolKey}
          narrationPassActive={screen.narrationPassActive}
          hidePool={hidden.pool}
          pointerTarget={pointerTarget}
          disabled={boardDisabled}
          onPoolItemClick={handlePoolItemClick}
        />
      </div>

      <div className="bucketing__cta">
        <BucketingSubmitButton
          hideSubmit={hidden.submit}
          pointerTarget={pointerTarget}
          isInstruction={isInstruction}
          isRepeating={isRepeating}
          boardIsDirty={boardIsDirty}
          submitEnabled={boardIsDirty && !boardDisabled}
          onSubmit={screen.handleSubmit}
        />
      </div>
    </div>
  );
};

export default Bucketing;
